import { useState, useEffect, useCallback } from 'react';
import './App.css';

function App() {
  const [activeTab, setActiveTab] = useState('home');
  const [showGame, setShowGame] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [gameState, setGameState] = useState({
    score: 0,
    level: 1,
    lives: 3,
    gameStarted: false,
    gameOver: false,
    enemies: [],
    bullets: [],
    powerUps: [],
    playerPosition: 50,
    enemySpeed: 1,
    bulletSpeed: 5,
    lastShot: 0,
    shootCooldown: 200
  });
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState('');

  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'contact', label: 'Contact us' },
  ];

  // Handle external links
  const handleExternalLink = (url) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Mobile menu functions
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleNavClick = (tabId) => {
    setActiveTab(tabId);
    closeMobileMenu();
  };

  // Handle Play Now - Show simple game
  const handlePlayNow = () => {
    setShowGame(true);
    resetGame();
  };

  // Game functions
  const resetGame = () => {
    setGameState({
      score: 0,
      level: 1,
      lives: 3,
      gameStarted: false,
      gameOver: false,
      enemies: [],
      bullets: [],
      powerUps: [],
      playerPosition: 50,
      enemySpeed: 1,
      bulletSpeed: 5,
      lastShot: 0,
      shootCooldown: 200
    });
  };

  const startGame = () => {
    setGameState(prev => ({
      ...prev,
      gameStarted: true,
      gameOver: false,
      enemies: [
        { id: 'enemy1', type: '👾', x: 20, y: 10, health: 1 },
        { id: 'enemy2', type: '🛸', x: 50, y: 15, health: 1 },
        { id: 'enemy3', type: '👽', x: 80, y: 20, health: 1 }
      ],
      powerUps: [
        { id: 'power1', type: '⭐', x: 30, y: 40, collected: false },
        { id: 'power2', type: '💎', x: 70, y: 45, collected: false }
      ]
    }));
  };

  const closeGame = () => {
    setShowGame(false);
    resetGame();
  };

  const movePlayer = useCallback((direction) => {
    if (!gameState.gameStarted || gameState.gameOver) return;
    
    setGameState(prev => ({
      ...prev,
      playerPosition: Math.max(5, Math.min(95, prev.playerPosition + (direction === 'left' ? -5 : 5)))
    }));
  }, [gameState.gameStarted, gameState.gameOver]);

  const shootBullet = useCallback(() => {
    if (!gameState.gameStarted || gameState.gameOver) return;
    
    const now = Date.now();
    if (now - gameState.lastShot < gameState.shootCooldown) return;
    
    setGameState(prev => ({
      ...prev,
      bullets: [...prev.bullets, {
        id: `bullet_${now}`,
        x: prev.playerPosition,
        y: 85,
        speed: prev.bulletSpeed
      }],
      lastShot: now
    }));
  }, [gameState.gameStarted, gameState.gameOver, gameState.lastShot, gameState.shootCooldown]);

  const spawnNewEnemies = (level) => {
    const enemyCount = Math.min(3 + level, 8);
    const enemies = [];
    
    for (let i = 0; i < enemyCount; i++) {
      const types = ['👾', '🛸', '👽', '🤖', '👻'];
      enemies.push({
        id: `enemy${i}_${level}`,
        type: types[Math.floor(Math.random() * types.length)],
        x: Math.random() * 80 + 10,
        y: Math.random() * 30 + 5,
        health: Math.floor(level / 3) + 1,
        maxHealth: Math.floor(level / 3) + 1
      });
    }
    
    return enemies;
  };

  const collectPowerUp = (powerUpId) => {
    if (!gameState.gameStarted || gameState.gameOver) return;
    
    setGameState(prev => ({
      ...prev,
      powerUps: prev.powerUps.filter(powerUp => powerUp.id !== powerUpId),
      score: prev.score + 50,
      lives: Math.min(5, prev.lives + 1)
    }));
  };

  // Keyboard controls
  const handleKeyPress = useCallback((e) => {
    if (!gameState.gameStarted || gameState.gameOver) return;
    
    switch(e.key) {
      case 'ArrowLeft':
      case 'a':
      case 'A':
        e.preventDefault();
        movePlayer('left');
        break;
      case 'ArrowRight':
      case 'd':
      case 'D':
        e.preventDefault();
        movePlayer('right');
        break;
      case ' ':
      case 'ArrowUp':
      case 'w':
      case 'W':
        e.preventDefault();
        shootBullet();
        break;
      default:
        break;
    }
  }, [gameState.gameStarted, gameState.gameOver, movePlayer, shootBullet]);

  // Add keyboard event listeners
  useEffect(() => {
    if (showGame && gameState.gameStarted) {
      window.addEventListener('keydown', handleKeyPress);
      return () => window.removeEventListener('keydown', handleKeyPress);
    }
  }, [showGame, gameState.gameStarted, handleKeyPress]);

  // Touch controls for mobile
  const handleTouchStart = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleTouchMove = useCallback((e) => {
    e.preventDefault();
    if (!gameState.gameStarted || gameState.gameOver) return;
    
    const touch = e.touches[0];
    const gameArea = e.currentTarget;
    const rect = gameArea.getBoundingClientRect();
    const touchX = ((touch.clientX - rect.left) / rect.width) * 100;
    
    setGameState(prev => ({
      ...prev,
      playerPosition: Math.max(5, Math.min(95, touchX))
    }));
  }, [gameState.gameStarted, gameState.gameOver]);

  const handleTouchEnd = useCallback((e) => {
    e.preventDefault();
    if (!gameState.gameStarted || gameState.gameOver) return;
    
    // Double tap to shoot
    const now = Date.now();
    if (now - gameState.lastShot > 100) {
      shootBullet();
    }
  }, [gameState.gameStarted, gameState.gameOver, gameState.lastShot, shootBullet]);

  // Game loop for bullet movement, enemy movement, and collision detection
  useEffect(() => {
    if (!gameState.gameStarted || gameState.gameOver) return;

    const gameLoop = setInterval(() => {
      setGameState(prev => {
        // Move bullets
        const newBullets = prev.bullets
          .map(bullet => ({ ...bullet, y: bullet.y - bullet.speed }))
          .filter(bullet => bullet.y > 0);

        // Move enemies downward
        let newEnemies = prev.enemies.map(enemy => ({
          ...enemy,
          y: enemy.y + prev.enemySpeed * 0.5
        }));

        // Check if any enemy reached the bottom (game over condition)
        const enemyReachedBottom = newEnemies.some(enemy => enemy.y > 90);
        let newLives = prev.lives;
        
        if (enemyReachedBottom) {
          newLives = prev.lives - 1;
          // Remove enemies that reached bottom
          newEnemies = newEnemies.filter(enemy => enemy.y <= 90);
        }

        // Check bullet-enemy collisions
        let newScore = prev.score;
        let bulletsToRemove = [];

        newBullets.forEach(bullet => {
          newEnemies.forEach((enemy, enemyIndex) => {
            const distance = Math.sqrt(
              Math.pow(bullet.x - enemy.x, 2) + Math.pow(bullet.y - enemy.y, 2)
            );
            
            if (distance < 8) { // Hit detection
              bulletsToRemove.push(bullet.id);
              newEnemies[enemyIndex] = { ...enemy, health: enemy.health - 1 };
              newScore += 10;
            }
          });
        });

        // Remove dead enemies
        newEnemies = newEnemies.filter(enemy => enemy.health > 0);

        // Remove hit bullets
        const finalBullets = newBullets.filter(bullet => !bulletsToRemove.includes(bullet.id));

        // Check game over condition
        if (newLives <= 0) {
          return {
            ...prev,
            gameOver: true,
            lives: 0,
            bullets: [],
            score: newScore
          };
        }

        // Check if all enemies are destroyed - spawn new wave
        if (newEnemies.length === 0) {
          const newLevel = prev.level + 1;
          newEnemies = spawnNewEnemies(newLevel);
          
          return {
            ...prev,
            bullets: finalBullets,
            enemies: newEnemies,
            score: newScore,
            level: newLevel,
            lives: newLives,
            enemySpeed: Math.min(prev.enemySpeed + 0.2, 3), // Increase enemy speed
            powerUps: [
              { id: `power1_${newLevel}`, type: '⭐', x: Math.random() * 60 + 20, y: Math.random() * 20 + 40, collected: false },
              { id: `power2_${newLevel}`, type: '💎', x: Math.random() * 60 + 20, y: Math.random() * 20 + 60, collected: false }
            ]
          };
        }

        return {
          ...prev,
          bullets: finalBullets,
          enemies: newEnemies,
          score: newScore,
          lives: newLives
        };
      });
    }, 50); // 20 FPS

    return () => clearInterval(gameLoop);
  }, [gameState.gameStarted, gameState.gameOver, spawnNewEnemies]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle form submission
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    setFormStatus('sending');
    
    try {
      // Prepare data for API according to DB schema
      const apiData = {
        name: formData.name,
        message: formData.message,
        phone: formData.phone,
        email: formData.email,
        userid: '', // You can set this based on your user system
        messagetype: 'contact_form' // Default message type for contact form
      };

      const response = await fetch('/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiData)
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Form submitted successfully:', result);
        setFormStatus('success');
        
        // Reset form after successful submission
        setFormData({
          name: '',
          phone: '',
          email: '',
          message: ''
        });
        
        // Clear success message after 5 seconds
        setTimeout(() => {
          setFormStatus('');
        }, 5000);
      } else {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setFormStatus('error');
      
      // Clear error message after 5 seconds
      setTimeout(() => {
        setFormStatus('');
      }, 5000);
    }
  };

  return (
    <div className="app">
      {/* Navigation */}
      <nav className="navbar">
        <div className="nav-container">
          <div className="logo">
            <div className="gamepad-icon">🎮</div>
            <span>GAMESCLUB</span>
          </div>

          {/* Desktop Navigation */}
          <ul className="nav-links desktop-nav">
            {navItems.map((item) => (
              <li key={item.id}>
                <button
                  className={`nav-button ${activeTab === item.id ? 'active' : ''}`}
                  onClick={() => handleNavClick(item.id)}
                  type="button"
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>

          {/* Desktop Subscribe Button */}
          <button 
            className="subscribe-btn desktop-subscribe"
            onClick={() => handleExternalLink('https://wolf-game.libroafrica.com/?productId=6301f46c-82b3-4a6a-b64a-d283573e6d1f&mktId=3a1f9fd7-9d57-4477-9515-eec18baccaee')}
          >
            SUBSCRIBE
          </button>

          {/* Mobile Hamburger Menu */}
          <button 
            className={`hamburger-menu ${mobileMenuOpen ? 'open' : ''}`}
            onClick={toggleMobileMenu}
            type="button"
            aria-label="Toggle mobile menu"
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>
        </div>

        {/* Mobile Menu Overlay */}
        <div className={`mobile-menu-overlay ${mobileMenuOpen ? 'open' : ''}`}>
          <div className="mobile-menu-content">
            <ul className="mobile-nav-links">
              {navItems.map((item) => (
                <li key={item.id}>
                  <button
                    className={`mobile-nav-button ${activeTab === item.id ? 'active' : ''}`}
                    onClick={() => handleNavClick(item.id)}
                    type="button"
                  >
                    {item.label}
                  </button>
                </li>
              ))}
            </ul>
            <button 
              className="mobile-subscribe-btn"
              onClick={() => {
                handleExternalLink('https://wolf-game.libroafrica.com/?productId=6301f46c-82b3-4a6a-b64a-d283573e6d1f&mktId=3a1f9fd7-9d57-4477-9515-eec18baccaee');
                closeMobileMenu();
              }}
            >
              SUBSCRIBE
            </button>
          </div>
        </div>

        {/* Mobile Menu Backdrop */}
        {mobileMenuOpen && (
          <div 
            className="mobile-menu-backdrop"
            onClick={closeMobileMenu}
          ></div>
        )}
      </nav>

      {/* Main Content */}
      <main className="main-content">
        {activeTab === 'home' && (
          <>
            {!showGame ? (
              <section className="hero-section">
                <div className="hero-content">
                  <div className="hero-text">
                    <h1>DIVE<br />INTO THE<br />GAME</h1>
                    <div className="hero-buttons">
                      <button className="play-btn" onClick={handlePlayNow}>
                        PLAY NOW
                      </button>
                      <button 
                        className="win-btn"
                        onClick={() => handleExternalLink('https://wolf-game.libroafrica.com/?productId=6301f46c-82b3-4a6a-b64a-d283573e6d1f&mktId=3a1f9fd7-9d57-4477-9515-eec18baccaee')}
                      >
                        WIN CASH <span className="arrow">↗</span>
                      </button>
                    </div>
                  </div>
                  <div className="hero-character">
                    <div className="vr-character">
                      <div className="character-body"></div>
                      <div className="vr-headset"></div>
                      <div className="character-hands">
                        <div className="hand left-hand"></div>
                        <div className="hand right-hand"></div>
                      </div>
                    </div>
                    <div className="planet"></div>
                    <div className="stars"></div>
                  </div>
                </div>
              </section>
            ) : (
              <section className="game-section">
                <div className="game-container">
                  <div className="game-header">
                    <h2>🚀 Space Defender</h2>
                    <div className="game-controls">
                      <button className="close-btn" onClick={closeGame} title="Close Game">
                        ✕
                      </button>
                      <button className="back-btn" onClick={closeGame}>
                        ← Back to Home
                      </button>
                    </div>
                  </div>
                  
                  <div className="advanced-game">
                    {/* Game Stats */}
                    <div className="game-stats">
                      <div className="stat">Score: {gameState.score}</div>
                      <div className="stat">Level: {gameState.level}</div>
                      <div className="stat">Lives: {'❤️'.repeat(gameState.lives)}</div>
                    </div>

                    {/* Game Area */}
                    <div 
                      className="game-area"
                      onTouchStart={handleTouchStart}
                      onTouchMove={handleTouchMove}
                      onTouchEnd={handleTouchEnd}
                    >
                      {!gameState.gameStarted && !gameState.gameOver && (
                        <div className="game-start">
                          <h3>🌌 Welcome to Space Defender!</h3>
                          <p>Defend Earth from alien invaders!</p>
                          <ul className="game-instructions">
                            <li>🎯 SPACEBAR/W to shoot • Tap screen to shoot (mobile)</li>
                            <li>⬅️➡️ Arrow keys/A/D to move • Drag to move (mobile)</li>
                            <li>⭐ Collect power-ups for extra lives (+50 points)</li>
                            <li>🛡️ Destroy all enemies to advance levels!</li>
                          </ul>
                          <button className="start-game-btn" onClick={startGame}>
                            🚀 Start Mission
                          </button>
                        </div>
                      )}

                      {gameState.gameStarted && !gameState.gameOver && (
                        <div className="game-play">
                          {/* Player Spaceship */}
                          <div 
                            className="player-ship" 
                            style={{ left: `${gameState.playerPosition}%` }}
                          >
                            🚀
                          </div>

                          {/* Bullets */}
                          {gameState.bullets.map(bullet => (
                            <div
                              key={bullet.id}
                              className="bullet"
                              style={{
                                left: `${bullet.x}%`,
                                top: `${bullet.y}%`
                              }}
                            >
                              💥
                            </div>
                          ))}

                          {/* Dynamic Enemies */}
                          {gameState.enemies.map(enemy => (
                            <div
                              key={enemy.id}
                              className="enemy"
                              style={{
                                left: `${enemy.x}%`,
                                top: `${enemy.y}%`
                              }}
                              title={`Health: ${enemy.health}/${enemy.maxHealth || 1}`}
                            >
                              {enemy.type}
                              {enemy.health > 1 && (
                                <div className="enemy-health">
                                  {'❤️'.repeat(enemy.health)}
                                </div>
                              )}
                            </div>
                          ))}

                          {/* Power-ups */}
                          {gameState.powerUps.filter(p => !p.collected).map(powerUp => (
                            <button
                              key={powerUp.id}
                              className="powerup"
                              style={{
                                left: `${powerUp.x}%`,
                                top: `${powerUp.y}%`
                              }}
                              onClick={() => collectPowerUp(powerUp.id)}
                              title="Collect power-up!"
                            >
                              {powerUp.type}
                            </button>
                          ))}

                          {/* Game Controls */}
                          <div className="game-movement">
                            <button 
                              className="move-btn" 
                              onClick={() => movePlayer('left')}
                            >
                              ⬅️ Move
                            </button>
                            <button 
                              className="shoot-btn" 
                              onClick={shootBullet}
                            >
                              🎯 Shoot
                            </button>
                            <button 
                              className="move-btn" 
                              onClick={() => movePlayer('right')}
                            >
                              Move ➡️
                            </button>
                          </div>

                          {/* Controls Help */}
                          <div className="controls-help">
                            <small>
                              🎮 A/D or ←/→ to move • SPACE/W to shoot • Touch: drag to move, tap to shoot
                            </small>
                          </div>
                        </div>
                      )}

                      {gameState.gameOver && (
                        <div className="game-over">
                          <div className="game-over-content">
                            <h3>🚀 Mission Complete!</h3>
                            <div className="final-stats">
                              <div className="stat-item">
                                <span className="stat-label">Final Score</span>
                                <span className="stat-value">{gameState.score.toLocaleString()}</span>
                              </div>
                              <div className="stat-item">
                                <span className="stat-label">Level Reached</span>
                                <span className="stat-value">{gameState.level}</span>
                              </div>
                              <div className="stat-item">
                                <span className="stat-label">Enemies Defeated</span>
                                <span className="stat-value">{Math.floor(gameState.score / 10)}</span>
                              </div>
                            </div>
                            
                            <div className="performance-message">
                              {gameState.score >= 500 ? (
                                <p>🏆 <strong>Legendary Performance!</strong> You're a true Space Defender!</p>
                              ) : gameState.score >= 200 ? (
                                <p>⭐ <strong>Great Job!</strong> You've got serious skills!</p>
                              ) : gameState.score >= 100 ? (
                                <p>👍 <strong>Well Done!</strong> Keep practicing to improve!</p>
                              ) : (
                                <p>🎯 <strong>Good Start!</strong> Try again to beat your score!</p>
                              )}
                            </div>

                            <div className="game-over-buttons">
                              <button className="restart-btn" onClick={() => {
                                resetGame();
                                startGame();
                              }}>
                                🔄 Play Again
                              </button>
                              <button className="exit-btn" onClick={closeGame}>
                                🏠 Exit to Home
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            )}
          </>
        )}

        {activeTab === 'about' && (
          <section className="about-section">
            <div className="about-container">
              <div className="about-badge">ABOUT US</div>
              <h2>Welcome to the Ultimate Gaming Experience</h2>
              <p>
                At GAMESCLUB, we're passionate about creating immersive gaming experiences that transport you to new worlds. 
                Our platform combines cutting-edge VR technology with classic gaming elements to deliver unforgettable adventures. 
                Whether you're a casual player looking for quick entertainment or a hardcore gamer seeking the next challenge, 
                we've got something special for you.
              </p>
              <br />
              <p>
                Join thousands of players worldwide who have already discovered the thrill of our games. 
                From space exploration to fantasy realms, our diverse collection of games offers endless possibilities. 
                Experience gaming like never before with stunning visuals, engaging storylines, and the chance to win real rewards.
              </p>
            </div>
          </section>
        )}

        {activeTab === 'contact' && (
          <section className="contact-section">
            <div className="contact-container">
              <h2>Have any questions?</h2>
              <form className="contact-form" onSubmit={handleFormSubmit}>
                {formStatus === 'success' && (
                  <div className="success-message">
                    ✅ Thank you! Your message has been sent successfully. We'll get back to you soon!
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="error-message">
                    ❌ Sorry, there was an error sending your message. Please try again or contact us directly.
                  </div>
                )}
                {formStatus === 'sending' && (
                  <div className="loading-message">
                    ⏳ Sending your message...
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="name">Name</label>
                    <input 
                      type="text" 
                      id="name" 
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required 
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone number</label>
                    <input 
                      type="tel" 
                      id="phone" 
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required 
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required 
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="message">Message</label>
                  <textarea 
                    rows="6" 
                    id="message" 
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  ></textarea>
                </div>
                <button type="submit" className="submit-btn" disabled={formStatus === 'sending'}>
                  {formStatus === 'sending' ? 'Sending...' : 'Let\'s Connect'} <span className="arrow">→</span>
                </button>
              </form>
            </div>
          </section>
        )}
      </main>

      {/* Footer */}
      <footer className="footer">
        <div className="footer-container">
          <p>Copyright © 2023 GAMESCLUB Company. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default App;
